import { useState } from "react";
import axios from "axios";
import type { DriverRegistration } from "../../../types/driver-registration.type";
import type { DriverRegistrationShare } from "../../../types/driver-registration-share.type";
import type { Organization } from "../../../types/organization.type";
import { usePassword } from "../../../hooks/use-password.hook";
import { shareDriverRegistration } from "../../../api/driver-registrations.api";
import "./share-modal.component.css";

type ShareModalComponentProps = {
  isOpen: boolean;
  onClose: () => void;
  driverRegistration: DriverRegistration;
  organizations: Organization[];
  onShared: (share: DriverRegistrationShare) => void;
};

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return (
      (error.response?.data as { message?: string } | undefined)?.message ??
      error.message
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Failed to share driver registration.";
}

export function ShareModalComponent({
  isOpen,
  onClose,
  driverRegistration,
  organizations,
  onShared,
}: ShareModalComponentProps) {
  const password = usePassword();
  const [sharingOrgId, setSharingOrgId] = useState<string>("");
  const [error, setError] = useState<string>("");

  if (!isOpen) {
    return undefined;
  }

  const shares = driverRegistration.shares ?? [];
  const orgsToShareWith = organizations.filter((org) => {
    const share = shares.find(
      (share) => share.organizationId === org.organizationId
    );
    return !share || share.declinedAt;
  });

  function handleShareClick(organizationId: string) {
    setSharingOrgId(organizationId);
    setError("");
    shareDriverRegistration(
      password,
      driverRegistration.driverRegistrationId,
      organizationId
    )
      .then((data) => {
        onShared(data.driverRegistrationShare);
      })
      .catch((error) => {
        setError(getErrorMessage(error));
      })
      .finally(() => {
        setSharingOrgId("");
      });
  }

  return (
    <div
      className="share-modal-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="share-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="share-modal-header">
          <h2 id="share-modal-title">
            Share {driverRegistration.firstName} {driverRegistration.lastName}
          </h2>
          <button
            type="button"
            className="share-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {error && (
          <p className="share-modal-error" role="alert">
            {error}
          </p>
        )}

        {orgsToShareWith.length === 0 ? (
          <p className="share-modal-empty">
            Already shared with every organization.
          </p>
        ) : (
          <ul className="share-modal-org-list">
            {orgsToShareWith.map((org) => (
              <li key={org.organizationId}>
                <button
                  type="button"
                  className="share-modal-org-button"
                  disabled={sharingOrgId !== ""}
                  onClick={() => handleShareClick(org.organizationId)}
                >
                  {sharingOrgId === org.organizationId
                    ? "Sharing…"
                    : org.displayName || org.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
